import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useSearchParams } from 'react-router-dom'
import { useClaudeWebSocket } from '../hooks/useClaudeWebSocket'
import type { ClaudeMessage, ClaudeWebSocketResponse, FileAttachment as FileAttachmentType, FnaFormData, ResultsSummary } from '../types'
import '../styles/app.css'
import { useAuthContext } from '../context/AuthContext'
import { useNewChatContext } from '../context/NewChatContext'
import { fetchFnaSummary } from '../services/fna'
import { fetchConversationMessages } from '../services/conversations'
import FileAttachment from '../components/FileAttachment'
import CopyButton from '../components/CopyButton'
import { mergeServerHistory } from '../utils/conversationMerge'

/**
 * Ask Claude AI Assistant page with WebSocket-based conversation.
 *
 * @returns JSX.Element The Ask Claude page component
 */
const AskClaudePage: React.FC = () => {
  const [messages, setMessages] = useState<ClaudeMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: 'user' | 'assistant', content: string }>>([])
  const [resultsSummary, setResultsSummary] = useState<ResultsSummary | null>(null)
  const { isAuthenticated } = useAuthContext()
  const [attachments, setAttachments] = useState<FileAttachmentType[]>([])

  const { newChatRequestId, clearNewChatRequest } = useNewChatContext()

  // Every transcript mutation goes through commitMessages so this ref is always
  // the committed transcript, even for updates that land between React renders.
  const messagesRef = useRef<ClaudeMessage[]>([])
  const commitMessages = useCallback((
    updater: (prev: ClaudeMessage[]) => ClaudeMessage[]
  ): ClaudeMessage[] => {
    const next = updater(messagesRef.current)
    messagesRef.current = next
    setMessages(next)
    return next
  }, [])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const lastUserMessageRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(0)
  const streamingContentRef = useRef<string>('')
  const streamingThinkingRef = useRef<string>('')
  // Streaming state lives in a ref, not state: chunks arrive faster than React
  // re-renders, so a state-based flag is still false for chunks that land
  // before the commit and each one starts a new bubble, losing the text
  // already streamed.
  const isStreamingRef = useRef<boolean>(false)
  // Track if current response was added to history to prevent duplicates
  const responseAddedToHistoryRef = useRef<boolean>(false)
  // Conversation whose transcript is currently on screen.
  const displayedConversationIdRef = useRef<number | null>(null)
  const {
    isConnected,
    isLoading,
    error,
    sendMessage,
    onMessage,
    onConversationLoaded,
    switchConversation,
    createNewConversation,
    reconnect
  } = useClaudeWebSocket(resultsSummary)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const targetId = Number(searchParams.get('conversation'))
    if (targetId && isConnected && targetId !== displayedConversationIdRef.current) {
      streamingContentRef.current = ''
      streamingThinkingRef.current = ''
      isStreamingRef.current = false
      switchConversation(targetId)
    }
  }, [searchParams, isConnected, switchConversation])

  const scrollToLastUserMessage = (): void => {
    lastUserMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const latest = messages[messages.length - 1]
      if (latest?.sender === 'user') {
        scrollToLastUserMessage()
      }
    }
    prevMessageCountRef.current = messages.length
  }, [messages])

  // Attempt to load FNA summary once authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    let isMounted = true

    const loadSummary = async () => {
      const summaryResponse = await fetchFnaSummary()
      if (summaryResponse && isMounted) {
        setResultsSummary(summaryResponse.summary)
        localStorage.setItem('results_summary', JSON.stringify(summaryResponse.summary))
        // Refresh form_data from the DB so Claude's profile context doesn't
        // serve stale values written by an earlier Gemini Live update.
        if (summaryResponse.form_data) {
          localStorage.setItem('form_data', JSON.stringify(summaryResponse.form_data))
        }
      } else if (isMounted) {
        const storedSummary = localStorage.getItem('results_summary')
        if (storedSummary) {
          try {
            setResultsSummary(JSON.parse(storedSummary))
          } catch (error) {
            console.error('Error parsing stored results summary:', error)
            localStorage.removeItem('results_summary')
          }
        }
      }
    }

    void loadSummary()

    return () => {
      isMounted = false
    }
  }, [isAuthenticated])


  // Handle new-chat requests from the sidebar context
  useEffect(() => {
    if (newChatRequestId > 0 && isConnected) {
      commitMessages(() => [])
      setConversationHistory([])
      streamingContentRef.current = ''
      streamingThinkingRef.current = ''
      isStreamingRef.current = false
      displayedConversationIdRef.current = null
      createNewConversation()
      clearNewChatRequest()
      // Drop the stale ?conversation= param, otherwise the switch effect above
      // pulls the user straight back into the conversation they just left.
      if (searchParams.has('conversation')) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('conversation')
        setSearchParams(nextParams, { replace: true })
      }
    }
  }, [newChatRequestId, isConnected, createNewConversation, clearNewChatRequest, searchParams, setSearchParams, commitMessages])

  /**
   * Pull the complete stored transcript for a conversation.
   *
   * The socket only replays the turns the backend still keeps verbatim, so a
   * conversation that has been summarized opens missing its earlier half. The
   * REST endpoint still has every message.
   *
   * @param convId - Conversation to backfill.
   * @returns Promise<void>
   */
  const backfillSummarizedHistory = useCallback(async (convId: number): Promise<void> => {
    try {
      const stored = await fetchConversationMessages(convId, 100, true)
      if (displayedConversationIdRef.current !== convId) {
        return
      }

      const fullTranscript: ClaudeMessage[] = stored
        .filter(message => message.role !== 'system')
        .map(message => ({
          content: message.content,
          sender: message.role === 'user' ? 'user' : 'assistant'
        }))

      commitMessages(prev => mergeServerHistory(fullTranscript, prev))
    } catch (error) {
      // Non-fatal: the socket's tail is already on screen.
      console.error('Error backfilling conversation history:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: { conversationId: convId }
      })
    }
  }, [commitMessages])

  // Handle conversation loading from server
  useEffect(() => {
    onConversationLoaded((convId, history, hasSummary) => {
      // Convert history to display messages
      const serverMessages: ClaudeMessage[] = history.map(msg => ({
        content: msg.content,
        sender: msg.role === 'user' ? 'user' : 'assistant'
      }))

      const isSameConversation = displayedConversationIdRef.current === convId
      displayedConversationIdRef.current = convId

      // A replay for a different conversation is a switch: show it as-is.
      // A replay for the conversation already on screen is a reconnect, and the
      // server only keeps the unsummarized tail, so merge instead of replacing
      // or the earlier turns vanish from the user's view.
      const nextMessages = commitMessages(prev => (
        isSameConversation ? mergeServerHistory(prev, serverMessages) : serverMessages
      ))

      setConversationHistory(nextMessages
        .filter(message => message.sender !== 'system')
        .map(message => ({
          role: message.sender === 'user' ? 'user' as const : 'assistant' as const,
          content: message.content
        })))

      // Opening a summarized conversation: the replay above is only its tail.
      if (!isSameConversation && hasSummary) {
        void backfillSummarizedHistory(convId)
      }
    })
  }, [onConversationLoaded, commitMessages, backfillSummarizedHistory])

  // Handle incoming WebSocket messages
  useEffect(() => {
    onMessage((data: ClaudeWebSocketResponse) => {
      if (data.error) {
        isStreamingRef.current = false
        streamingContentRef.current = ''
        streamingThinkingRef.current = ''
        commitMessages(prev => [...prev, {
          content: `Error: ${data.error}`,
          sender: 'system'
        }])
        return
      }

      // Display assistant's full response (non-streaming fallback)
      const response = data.response
      if (response) {
        isStreamingRef.current = false
        streamingContentRef.current = ''
        streamingThinkingRef.current = ''
        commitMessages(prev => [...prev, {
          content: response,
          sender: 'assistant'
        }])
        // Only add to history if not already added (prevents duplicates)
        if (!responseAddedToHistoryRef.current) {
          responseAddedToHistoryRef.current = true
          setConversationHistory(prev => [...prev, {
            role: 'assistant',
            content: response
          }])
        }
      }

      // Reasoning summary. Arrives before the answer text, so on most turns
      // this is what creates the assistant bubble; the text handler below then
      // fills in content on the same message.
      if (data.type === 'thinking' && data.text) {
        streamingThinkingRef.current += data.text
        if (!isStreamingRef.current) {
          isStreamingRef.current = true
          streamingContentRef.current = ''
          responseAddedToHistoryRef.current = false
          commitMessages(prev => [...prev, {
            content: '',
            thinking: streamingThinkingRef.current,
            sender: 'assistant'
          }])
        } else {
          commitMessages(prev => {
            const newMessages = [...prev]
            for (let i = newMessages.length - 1; i >= 0; i--) {
              if (newMessages[i].sender === 'assistant') {
                newMessages[i] = {
                  ...newMessages[i],
                  thinking: streamingThinkingRef.current
                }
                break
              }
            }
            return newMessages
          })
        }
        return
      }

      // Handle streaming response chunks
      const chunk = data.chunk
      if (chunk) {
        if (!isStreamingRef.current) {
          // First chunk - create new assistant message and reset history flag
          isStreamingRef.current = true
          streamingContentRef.current = chunk
          responseAddedToHistoryRef.current = false  // Reset for new response
          commitMessages(prev => [...prev, {
            content: chunk,
            thinking: streamingThinkingRef.current || undefined,
            sender: 'assistant'
          }])
        } else {
          // Subsequent chunks - append to streaming content
          streamingContentRef.current += chunk
          commitMessages(prev => {
            // Find the last assistant message and update it
            const newMessages = [...prev]
            for (let i = newMessages.length - 1; i >= 0; i--) {
              if (newMessages[i].sender === 'assistant') {
                newMessages[i] = {
                  ...newMessages[i],
                  content: streamingContentRef.current
                }
                break
              }
            }
            return newMessages
          })
        }
      }

      // Handle completion of streaming
      if (data.complete) {
        // Only add to history if not already added (prevents duplicates)
        if (streamingContentRef.current && !responseAddedToHistoryRef.current) {
          responseAddedToHistoryRef.current = true
          setConversationHistory(prev => [...prev, {
            role: 'assistant',
            content: streamingContentRef.current
          }])
        }
        isStreamingRef.current = false
        streamingContentRef.current = ''
        streamingThinkingRef.current = ''
      }
    })
  }, [onMessage, commitMessages])

  /**
   * Handles sending a message to Claude.
   *
   * @param text - The message text to send
   */
  const handleSendMessage = async (text: string): Promise<void> => {
    if ((!text.trim() && attachments.length === 0) || isLoading || !isConnected) return

    // Build display content for user message (including attachment indicators)
    let displayContent = text
    if (attachments.length > 0) {
      const attachmentNames = attachments.map(a => a.filename).join(', ')
      if (text.trim()) {
        displayContent = `${attachmentNames}\n\n${text}`
      } else {
        displayContent = attachmentNames
      }
    }

    // Add user message to chat immediately
    commitMessages(prev => [...prev, { content: displayContent, sender: 'user' }])
    setInputValue('')
    
    // Reset response tracking for new message
    responseAddedToHistoryRef.current = false
    
    // Capture current attachments before clearing
    const currentAttachments = [...attachments]
    setAttachments([])

    // Snapshot prior history BEFORE adding current turn to avoid duplicate-turn bug.
    // The current message is sent separately as `message`; history should contain only prior turns.
    const priorHistory = conversationHistory.slice(-10)
    setConversationHistory(prev => [...prev, { role: 'user' as const, content: text || '(attached files)' }])

    try {
      // Get form data from localStorage if available
      let formData: FnaFormData | null = null
      try {
        const storedData = localStorage.getItem('form_data')
        if (storedData) {
          formData = JSON.parse(storedData)
        }
      } catch (error) {
        console.error('Error parsing form data from localStorage:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          context: { error }
        })
      }

      await sendMessage(text, priorHistory, formData, currentAttachments)
    } catch (error) {
      console.error('Error sending message:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: { error }
      })
      commitMessages(prev => [...prev, {
        content: 'Sorry, I encountered an error processing your message.',
        sender: 'system'
      }])
    }
  }

  /**
   * Handles button click for text input submission.
   */
  const handleSubmit = (): void => {
    handleSendMessage(inputValue)
  }

  return (
    <div className="ask-claude-layout">
      {/* Main Chat Area */}
      <div className="ask-claude-main">
        <div className="conversation-section">
          <div className="chat-header">
            <span
              className={`connection-status ${isConnected ? 'status-connected' : 'status-disconnected'}`}
              role="status"
            >
              <span className="connection-dot" aria-hidden="true" />
              {isConnected ? 'Connected' : 'Reconnecting'}
            </span>
          </div>

          <div className="conversation-interface">
            {/* Messages Container */}
            <div className="conversation-messages">
              {messages.map((message, index) => (
                <div
                  key={index}
                  ref={message.sender === 'user' ? lastUserMessageRef : undefined}
                  className={`message ${
                    message.sender === 'user'
                      ? 'user-message'
                      : message.sender === 'system'
                        ? 'system-message'
                        : 'assistant-message'
                  }`}
                >
                  <div className="message-sender">
                    {message.sender === 'user' ? 'You' : message.sender === 'system' ? 'System' : 'Claude'}
                  </div>
                  {message.sender === 'assistant' && message.thinking && (
                    <details className="message-thinking">
                      <summary>Reasoning</summary>
                      <div className="message-thinking-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.thinking}</ReactMarkdown>
                      </div>
                    </details>
                  )}
                  <div className="message-content">
                    {message.sender === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                    )}
                  </div>
                  {message.sender === 'assistant' && message.content && (
                    <div className="message-actions">
                      <CopyButton value={message.content} />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="loading-indicator">
                  <div className="message-sender">Claude</div>
                  <div>Thinking...</div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer: attachments + text input dock together */}
            <div className="composer-dock">
            <FileAttachment
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              disabled={isLoading || !isConnected}
            />

            <div className="text-input-area">
              <textarea
                id="text-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message here..."
                disabled={isLoading || !isConnected}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                rows={2}
              />
              <button
                type="button"
                id="sendBtn"
                onClick={handleSubmit}
                disabled={isLoading || !isConnected || (!inputValue.trim() && attachments.length === 0)}
              >
                Send
              </button>
            </div>
            </div>
            {error && (
              <div className="input-error">
                <div>{error}</div>
                {!isConnected && (
                  <div className="websocket-error-actions">
                    <button
                      type="button"
                      className="btn btn-secondary websocket-retry-btn"
                      onClick={reconnect}
                    >
                      Retry Connection
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AskClaudePage
